import React, { useCallback, useMemo } from 'react';
import { Box, CircularProgress, IconButton, Paper, Stack, Typography } from '@mui/material';
import RawFilePreview from 'c-components/FileBrowser/RawFilePreview';
import Avatar from 'c-components/Avatar';
import FileIcon from 'c-components/FileBrowser/FileIcon';
import { Upload } from '@mui/icons-material';
import { popShotToFile } from 'c-main/Lib';
import { useAPIClientRequest, useDateUtils } from 'c-hooks';
import { useDeleteEntityData } from 'c-data';
import type { CampaignPopShot } from 'c-sdk';
import { Checkbox, DeleteEntityDialog } from 'c-components';
import { useBoolean } from 'react-hanger';
import { useCommonTranslation } from 'c-translation';
import { css } from '@emotion/react';
import { deleteIcon } from 'c-main/icons';
import { onClickStopPropagation } from 'c-lib';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import apiClient from 'c-data/apiClient';
import to from 'await-to-js';
import { useUserPermissions } from 'c-auth-module/Hooks';
import {
    canEditOrDeletePopShot,
    POP_SHOT_DELETE_PERMISSIONS,
    POP_SHOT_UPDATE_PERMISSIONS,
    popShotRetypePayload,
} from '../CampaignPopShot/popShotLib';

type Props = {
    popShot: CampaignPopShot;
    index: number;
    onClick: (index: number) => void;
    onDelete: () => void;
    onUpdated: () => void;
};

const deletePerms = POP_SHOT_DELETE_PERMISSIONS;
const updatePerms = POP_SHOT_UPDATE_PERMISSIONS;

const controlsClass = 'controls';

const styles = css(`
    .${controlsClass} {
        pointer-events: none;
        opacity: 0;
        transition: all 200ms;
        z-index: 10;
    }
    &:hover {
        .${controlsClass} {
            visibility: visible;
            opacity: 1;
            pointer-events: auto;
        }
    }

`);

const paperSx = { borderRadius: 1, cursor: 'pointer', position: 'relative' };

const PopShotTile: React.FC<Props> = ({ popShot, index, onClick, onDelete, onUpdated }) => {
    const popshotFile = useMemo(() => popShotToFile(popShot), [popShot]);
    const { formatDateString } = useDateUtils();
    const { deleteEntity, getDeletingById } = useDeleteEntityData('CampaignPopShot');
    const onClicked = useCallback(() => onClick(index), [onClick, index]);
    const { value: isDeleteOpen, setFalse: closeDelete, setTrue: openDelete } = useBoolean(false);

    const onDeleteClicked = useCallback(
        e => {
            e.stopPropagation();
            openDelete();
        },
        [openDelete],
    );
    const t = useCommonTranslation();
    const description = useMemo(
        () =>
            t('Modules.Main.Campaigns.PopShots.delete.description', {
                name: popShot?.file?.filename,
            }),
        [popShot, t],
    );

    const isPro = useMemo(() => popShot.type === 'professional', [popShot]);
    const { start, isLoading, hasFailed, error, requestStateRef } = useAPIClientRequest(
        apiClient.Entities.CampaignPopShot.update,
    );

    const togglePro = useCallback(
        async (state: boolean) => {
            const [err, succ] = await to(start(popShotRetypePayload([popShot], state)));

            if (!err) onUpdated();
        },
        [start, popShot, onUpdated],
    );

    const { hasAll } = useUserPermissions();
    const canEditOrDelete = useMemo(() => canEditOrDeletePopShot(hasAll), [hasAll]);

    const controls = useMemo(
        () => (
            <Box
                position="absolute"
                top={2}
                right={2}
                className={controlsClass}
                onClick={onClickStopPropagation}
            >
                <Paper elevation={2}>
                    <Stack direction="row" gap={1} p={1}>
                        <IfHasAllPermissions permissions={deletePerms}>
                            <IconButton size="small" color="error" onClick={onDeleteClicked}>
                                {deleteIcon}
                            </IconButton>
                        </IfHasAllPermissions>
                        <IfHasAllPermissions permissions={updatePerms}>
                            <Checkbox
                                disabled={isLoading}
                                isBoolean
                                size="small"
                                formControlLabelSx={{
                                    ml: 0,
                                    '& .MuiTypography-root': { fontSize: '0.8rem' },
                                }}
                                sx={{ p: 0.4, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                                value={isPro}
                                label={t('Modules.Main.Campaigns.PopShots.table.isPro')}
                                icon={isLoading ? <CircularProgress size={24} /> : undefined}
                                checkedIcon={isLoading ? <CircularProgress size={24} /> : undefined}
                                onChange={togglePro}
                            />
                        </IfHasAllPermissions>
                    </Stack>
                </Paper>
            </Box>
        ),
        [isLoading, isPro, onDeleteClicked, t, togglePro],
    );

    return (
        <>
            <DeleteEntityDialog
                entityName="CampaignPopShot"
                title="Modules.Main.Campaigns.PopShots.delete.title"
                description={description}
                show={isDeleteOpen}
                form={null}
                entityId={popShot.id}
                onClose={closeDelete}
                onSuccess={onDelete}
            />
            <Paper key={popshotFile.id} elevation={2} sx={paperSx} onClick={onClicked} css={styles}>
                {canEditOrDelete && controls}
                <Box display="flex" justifyContent="center">
                    <RawFilePreview
                        file={popshotFile}
                        showPdfEmbed={false}
                        showVideoPreview={false}
                        maxHeight={170}
                        imgStyles={{
                            height: 170,
                        }}
                        preferredSize="full"
                        fallback={
                            <Avatar sx={{ borderRadius: 1, height: 170, width: 'auto' }}>
                                <FileIcon file={popshotFile} />
                            </Avatar>
                        }
                    />
                </Box>
                <Stack
                    height={64}
                    gap={1}
                    justifyContent="space-between"
                    alignItems="center"
                    p={1.5}
                >
                    {popShot.lineItem != null && (
                        <Box width="100%" maxWidth={170} mx="auto" overflow="hidden">
                            <Typography variant="caption" align="center" noWrap>
                                <b>{popShot.lineItem.name}</b>
                            </Typography>
                        </Box>
                    )}
                    <Stack
                        gap={1}
                        justifyContent="center"
                        overflow="hidden"
                        width="100%"
                        maxWidth={170}
                        mx="auto"
                        textAlign="center"
                    >
                        <Typography noWrap variant="caption" align="center">
                            {popShot.file.filename}
                        </Typography>
                        <Box mx="auto">
                            <Typography
                                noWrap
                                variant="caption"
                                align="center"
                                sx={{ display: 'flex', alignItems: 'center' }}
                            >
                                <Upload fontSize="inherit" />{' '}
                                {formatDateString({
                                    date: popShot.created_at,
                                    removeTime: false,
                                })}
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>
            </Paper>
        </>
    );
};

export default PopShotTile;
